import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./design/FranchiseListing.css";
import InquiryForm from "./EnquiryForm";
import "./design/ListingInquiry.css";
import { getImageUrl, getApiUrl } from "../utils/api";
import { FaPhoneAlt, FaFileAlt } from "react-icons/fa";

// --- UTILITY FUNCTIONS ---
const formatIndianCurrency = (number) => {
  if (!number || isNaN(number)) return "—";
  if (number >= 10000000) return `₹ ${(number / 10000000).toFixed(1)} cr`;
  if (number >= 100000) return `₹ ${Math.round(number / 100000)} lacs`;
  return `₹ ${number.toLocaleString("en-IN")}`;
};

// --- COMPONENT ---
export default function CategoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const mas_cat = params.get("mas_cat");
  const cat = params.get("cat");
  const sub = params.get("sub");

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMobileForm, setShowMobileForm] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  // Add unique page identifier to body when component mounts
  useEffect(() => {
    document.body.setAttribute('data-fl-page', 'active');
    return () => {
      document.body.removeAttribute('data-fl-page');
    };
  }, []);

  useEffect(() => {
    const fetchBrands = async () => {
      setLoading(true);
      setError(null);

      let url = getApiUrl("get-filtered-brands.php?");
      if (mas_cat) url += `mas_cat=${mas_cat}&`;
      if (cat) url += `cat=${cat}&`;
      if (sub) url += `sub=${sub}&`;

      try {
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await res.json();
        if (Array.isArray(data)) {
          setBrands(data);
        } else if (data.success && Array.isArray(data.brands)) {
          setBrands(data.brands);
        } else {
          setError(data.message || "No brands found with these filters.");
        }
      } catch (err) {
        console.error("Error fetching brands:", err);
        setError("Failed to fetch brands. Please try again.");
        setBrands([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [mas_cat, cat, sub]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showMobileForm) {
      document.body.classList.add('fl-modal-open');
    } else {
      document.body.classList.remove('fl-modal-open');
    }
    return () => document.body.classList.remove('fl-modal-open');
  }, [showMobileForm]);

  const handleKnowMore = (register_id) => {
    navigate(`/brand/${register_id}`);
  };

  const handleImageError = (brandId) => {
    setImageErrors(prev => ({ ...prev, [brandId]: true }));
  };

  if (loading) {
    return (
      <div className="fl-loading-message">
        <div className="fl-loading-spinner"></div>
        Loading franchises...
      </div>
    );
  }

  if (error) {
    return <div className="fl-error-message">{error}</div>;
  }

  if (brands.length === 0) {
    return (
      <div className="fl-no-brands-message">
        No franchises found matching your criteria.
      </div>
    );
  }

  return (
    <>
      <div className="fl-container">
        <div className="fl-main">
          <div className="fl-header">
            <h2 className="fl-heading">Franchises</h2>
          </div>

          <div className="fl-grid">
            {brands.map((brand, i) => (
              <div className="fl-card" key={brand.id || i}>
                <div className="fl-img-wrap">
                  <a 
                    href={`/brand/${brand.register_id}`} 
                    onClick={(e) => { 
                      e.preventDefault(); 
                      handleKnowMore(brand.register_id); 
                    }}
                  >
                    <img
                      src={!imageErrors[brand.id] ? getImageUrl(brand.logo) : 'https://via.placeholder.com/200?text=No+Image'}
                      alt={brand.name || "Brand"}
                      onError={() => handleImageError(brand.id)}
                      loading="lazy"
                    />
                  </a>
                </div>
                <div className="fl-category">{brand.sector || "—"}</div>
                <div className="fl-content">
                  <h3 className="fl-brand-name">{brand.name || "Unknown Brand"}</h3>
                  <div className="fl-detail">
                    <div className="fl-biz-field">
                      <span className="fl-label">Investment:</span>
                      <span className="fl-value">
                        {formatIndianCurrency(brand.min_investment)} - {formatIndianCurrency(brand.max_investment)}
                      </span>
                    </div>
                    <div className="fl-biz-field">
                      <span className="fl-label">Area:</span>
                      <span className="fl-value">
                        {brand.min_area || 0} - {brand.max_area || 0} sq.ft
                      </span>
                    </div>
                    <div className="fl-biz-field">
                      <span className="fl-label">Outlets:</span>
                      <span className="fl-value">{brand.total_outlets || 0}</span>
                    </div>
                  </div>

                  <div className="fl-buttons-row">
                    <button
                      className="fl-know-more-btn"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        handleKnowMore(brand.register_id); 
                      }}
                      type="button"
                    >
                      Know More
                    </button>
                    
                    <button
                      className="fl-call-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = 'tel:+919403890794';
                      }}
                      type="button"
                    >
                      <FaPhoneAlt style={{ marginRight: "6px" }} />
                      Call Us
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop Sidebar - Hidden on mobile */}
        <div className="fl-sidebar">
          <InquiryForm />
        </div>
      </div>

      {/* Mobile Floating Instant Apply Button - BOTTOM CENTER */}
      <button
        className="fl-instant-apply-btn"
        onClick={() => setShowMobileForm(true)}
      >
        <FaFileAlt /> Instant Apply
      </button>

      {/* Mobile Form Overlay */}
      {showMobileForm && (
        <div className="fl-form-overlay" onClick={() => setShowMobileForm(false)}>
          <div className="fl-form-overlay-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="fl-close-form-btn"
              onClick={() => setShowMobileForm(false)}
            >
              ✕
            </button>
            <InquiryForm />
          </div>
        </div>
      )}
    </>
  );
}